// Bare-row (GONE) queue-rescue synthesis (main #830 / Codex P1).
//
// When a queued capture_items op's LIVE local row was already sync-deleted (a
// re-seed), the live-row JOIN in rescue-held-captures.ts drops it — but the count
// can still be preserved from the queued op itself: opData holds the count
// (quantity / is_zero_confirmed / notes) and the trackMetadata snapshot holds the
// FK context the increment recorded. Extracted from rescue-held-captures.ts so that
// file stays within the line cap; imported back by `readQueuedItemsSplit`.

import type { RecoveryDumpItem } from './rescue-held-captures'

function str(value: unknown): string | null {
  return value === null || value === undefined ? null : String(value)
}

/** Best-effort parse of the queued op's trackMetadata snapshot (the FK-context
 * JSON the increment wrote). Never throws — a bad payload just yields {}. */
function parseQueuedMetadata(metadata: string | undefined): Record<string, unknown> {
  if (metadata === undefined || metadata === '') return {}
  try {
    const parsed: unknown = JSON.parse(metadata)
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

// A gone-row queued op is only worth synthesizing if it actually carries something
// to preserve — a count (quantity / is_zero_confirmed) in opData, or the FK-context
// metadata snapshot. A bare op with neither (e.g. a normalized entry with no
// payload) is NOT fabricated into an empty dump (no-fabrication rule;
// rescue-upload-queue.test.ts "no fabricated dump"). (Codex P1)
export function hasPreservablePayload(entry: {
  opData?: Record<string, unknown> | null
  metadata?: string
}): boolean {
  const data = entry.opData ?? {}
  return (
    data['quantity'] != null ||
    data['is_zero_confirmed'] != null ||
    (entry.metadata !== undefined && entry.metadata !== '')
  )
}

// Synthesize a dump item for a queued capture_items op whose LIVE row is GONE — the
// bare-row case the live-row JOIN silently dropped before (Codex P1). The count comes
// from the queued op's opData; the FK context from opData (a queued CREATE carries it)
// falling back to the trackMetadata snapshot (a thin increment PATCH carries it only
// there). `raw` keeps the verbatim op as the forensic backstop, like the quarantine path.
export function toQueuedDumpItemFromCrud(entry: {
  id: string
  opData?: Record<string, unknown> | null
  metadata?: string
}): RecoveryDumpItem {
  const data = entry.opData ?? {}
  const meta = parseQueuedMetadata(entry.metadata)
  const zeroRaw = data['is_zero_confirmed']
  return {
    originalItemId: entry.id,
    captureSessionId:
      str(data['capture_session_id']) ?? str(meta['capture_session_id']),
    siteId: str(data['site_id']) ?? str(meta['site_id']),
    buildingId: str(data['building_id']) ?? str(meta['building_id']),
    spaceId: str(data['space_id']) ?? str(meta['space_id']),
    assetTypeId: str(data['asset_type_id']) ?? str(meta['asset_type_id']),
    quantity: str(data['quantity']),
    isZeroConfirmed:
      zeroRaw === undefined || zeroRaw === null
        ? null
        : zeroRaw === 1 || zeroRaw === true
          ? 1
          : 0,
    notes: str(data['notes']),
    capturedAt: str(data['captured_at']) ?? str(meta['captured_at']),
    orphanedAt: null,
    reason: 'logout_queue_rescue',
    sourceDeviceId: null,
    spaceDisplayName: null,
    roomTypeId: null,
    floorId: null,
    serialNumber: null,
    sessionBuildingId: null,
    sessionSiteId: null,
    sessionStartedAt: null,
    raw: { ...data, _metadata: entry.metadata ?? null },
  }
}
